import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  FormControlLabel,
  Radio,
  RadioGroup,
  MenuItem,
  FormHelperText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Switch,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import PageLayout from './ui/PageLayout';
import api, { enhancedApi } from '../api';

const getValueFields = (value) => {
  if (typeof value === 'boolean') return { boolean_value: value };
  if (typeof value === 'number') return { number_value: value };
  if (value && typeof value === 'object' && value instanceof Date) return { date_value: value.toISOString().split('T')[0] };
  return { text_value: String(value ?? '') };
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getProgressColor = (pct) => {
  if (pct < 50) return 'error';
  if (pct < 80) return 'warning';
  return 'success';
};

const OrganizerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [organizer, setOrganizer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, doc: null });
  const [deleting, setDeleting] = useState(false);
  const [attachmentsSnackbar, setAttachmentsSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dragOver, setDragOver] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);

  const fetchAttachments = useCallback(async () => {
    if (!id) return;
    try {
      setAttachmentsLoading(true);
      const res = await enhancedApi.getOrganizerDocuments(id);
      const data = res.data?.data ?? res.data ?? [];
      setAttachments(Array.isArray(data) ? data : []);
    } catch {
      setAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  }, [id]);

  const fetchOrganizer = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/organizers/${id}/`);
      if (response.data.success) {
        const data = response.data.data;
        setOrganizer(data);
        const initialResponses = {};
        (data.responses || []).forEach((r) => {
          initialResponses[r.field] = r;
        });
        setResponses(initialResponses);
        if (data.sections?.length && expandedSection === null) {
          setExpandedSection(data.sections[0].id);
        }
      } else {
        setError('Failed to fetch organizer');
      }
    } catch (err) {
      setError('Error fetching organizer');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrganizer();
  }, [id]);

  useEffect(() => {
    if (id && organizer) fetchAttachments();
  }, [id, organizer, fetchAttachments]);

  const handleResponseChange = async (fieldId, value) => {
    if (!id) return;
    try {
      setSaving(true);
      let response;
      if (value instanceof File) {
        const formData = new FormData();
        formData.append('field', fieldId);
        formData.append('file_value', value);
        response = await api.post(`/api/organizers/${id}/responses/`, formData);
      } else {
        response = await api.post(`/api/organizers/${id}/responses/`, {
          field: fieldId,
          ...getValueFields(value),
        });
      }
      if (response.data.success) {
        setResponses((prev) => ({ ...prev, [fieldId]: response.data.data }));
        setLastSavedAt(new Date());
      }
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  const getFieldValue = (fieldId) => {
    const response = responses[fieldId];
    if (!response) return '';
    return response.text_value ?? response.number_value ?? response.boolean_value ?? response.date_value ?? response.file_value ?? '';
  };

  const isFieldVisible = (field) => {
    const depFieldId = field.conditional_field_id ?? field.conditional_field;
    if (!depFieldId) return true;
    const depValue = getFieldValue(depFieldId);
    const expected = field.conditional_value;
    if (expected === undefined || expected === null) return true;
    if (Array.isArray(expected)) return expected.some((v) => String(depValue) === String(v) || depValue === v);
    return String(depValue) === String(expected) || depValue === expected;
  };

  const getSectionProgress = (section) => {
    const fields = (section.fields || []).filter(isFieldVisible);
    const required = fields.filter((f) => f.is_required);
    const answered = required.filter((f) => {
      const v = getFieldValue(f.id);
      return v !== '' && v !== undefined && v !== null;
    });
    return { answered: answered.length, total: required.length };
  };

  const getOverallProgress = () => {
    const sections = organizer?.sections ?? [];
    let totalRequired = 0;
    let answeredRequired = 0;
    sections.forEach((sec) => {
      const prog = getSectionProgress(sec);
      totalRequired += prog.total;
      answeredRequired += prog.answered;
    });
    return { answered: answeredRequired, total: totalRequired };
  };

  const handleFileUpload = async (files) => {
    if (!id || !files?.length) return;
    setUploadError(null);
    setUploadLoading(true);
    try {
      for (const file of Array.from(files)) {
        await enhancedApi.uploadOrganizerDocument(id, file);
      }
      await fetchAttachments();
      setAttachmentsSnackbar({ open: true, message: 'File(s) uploaded successfully', severity: 'success' });
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed');
      setAttachmentsSnackbar({ open: true, message: 'Upload failed', severity: 'error' });
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer?.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDeleteConfirm = (doc) => setDeleteConfirm({ open: true, doc });
  const handleDeleteConfirmClose = () => setDeleteConfirm({ open: false, doc: null });

  const handleDeleteDocument = async () => {
    const { doc } = deleteConfirm;
    if (!id || !doc) return;
    setDeleting(true);
    try {
      await enhancedApi.deleteOrganizerDocument(id, doc.id);
      await fetchAttachments();
      setAttachmentsSnackbar({ open: true, message: 'Document deleted', severity: 'success' });
    } catch {
      setAttachmentsSnackbar({ open: true, message: 'Delete failed', severity: 'error' });
    } finally {
      setDeleting(false);
      handleDeleteConfirmClose();
    }
  };

  const getDownloadUrl = (doc) => {
    const fileUrl = doc.file;
    if (!fileUrl) return null;
    if (typeof fileUrl === 'string' && fileUrl.startsWith('http')) return fileUrl;
    const base = process.env.REACT_APP_API_URL || '';
    return `${base}${String(fileUrl).startsWith('/') ? '' : '/'}${fileUrl}`;
  };

  const handleSaveProgress = async () => {
    if (!id || saving) return;
    setSaving(true);
    try {
      const res = await api.get(`/api/organizers/${id}/`);
      if (res.data?.success && res.data?.data) {
        const initialResponses = {};
        (res.data.data.responses || []).forEach((r) => {
          initialResponses[r.field] = r;
        });
        setResponses(initialResponses);
        setLastSavedAt(new Date());
      }
    } catch {
      // no-op
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitOrganizer = async () => {
    if (!id) return;
    setSubmitLoading(true);
    try {
      await api.patch(`/api/organizers/${id}/`, { status: 'completed' });
      setOrganizer((prev) => (prev ? { ...prev, status: 'completed' } : null));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit organizer');
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderField = (field) => {
    const value = getFieldValue(field.id);

    switch (field.field_type) {
      case 'text':
      case 'email':
        return (
          <TextField
            fullWidth
            size="small"
            type={field.field_type === 'email' ? 'email' : 'text'}
            value={value}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.is_required}
          />
        );
      case 'number':
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            value={value}
            onChange={(e) => handleResponseChange(field.id, parseFloat(e.target.value) || 0)}
            placeholder={field.placeholder}
            required={field.is_required}
            inputProps={{ min: field.min_value, max: field.max_value }}
          />
        );
      case 'date':
        return (
          <TextField
            fullWidth
            size="small"
            type="date"
            value={value || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value || null)}
            required={field.is_required}
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'boolean':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={value === true}
                  onChange={(e) => handleResponseChange(field.id, e.target.checked)}
                  color="primary"
                />
              }
              label={value === true ? 'Yes' : 'No'}
            />
          </Box>
        );
      case 'dropdown':
        return (
          <TextField
            select
            fullWidth
            size="small"
            value={value}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            required={field.is_required}
          >
            <MenuItem value="">Select an option</MenuItem>
            {(field.options || []).map((option, idx) => (
              <MenuItem key={idx} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        );
      case 'textarea':
        return (
          <TextField
            fullWidth
            size="small"
            multiline
            rows={4}
            value={value}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.is_required}
          />
        );
      case 'file_upload':
        return (
          <Box>
            <input
              type="file"
              id={`field-file-${field.id}`}
              style={{ display: 'none' }}
              onChange={(e) => {
                const files = e.target.files;
                if (files?.length) handleResponseChange(field.id, files[0]);
                e.target.value = '';
              }}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              onClick={() => document.getElementById(`field-file-${field.id}`)?.click()}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              {value ? 'Change file' : 'Upload file'}
            </Button>
            {value && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {typeof value === 'object' && value?.name ? value.name : 'File uploaded'}
              </Typography>
            )}
          </Box>
        );
      default:
        return (
          <TextField
            fullWidth
            size="small"
            type="text"
            value={value}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            required={field.is_required}
          />
        );
    }
  };

  const formatSavedAgo = (date) => {
    if (!date) return '';
    const sec = Math.floor((new Date() - date) / 1000);
    if (sec < 5) return 'Saved just now';
    if (sec < 60) return `Saved ${sec}s ago`;
    if (sec < 3600) return `Saved ${Math.floor(sec / 60)}m ago`;
    return `Saved at ${date.toLocaleTimeString()}`;
  };

  const getStatusColor = (status) => {
    const map = { draft: 'default', sent_to_client: 'info', in_progress: 'warning', completed: 'success', archived: 'default' };
    return map[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const map = { draft: 'Draft', sent_to_client: 'Sent', in_progress: 'In Progress', completed: 'Completed', archived: 'Archived' };
    return map[status] || status;
  };

  if (loading) {
    return (
      <PageLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <Alert severity="error">{error}</Alert>
      </PageLayout>
    );
  }

  if (!organizer) {
    return (
      <PageLayout>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" gutterBottom>Organizer not found</Typography>
          <Button variant="contained" onClick={() => navigate(-1)} sx={{ textTransform: 'none' }}>
            Go Back
          </Button>
        </Box>
      </PageLayout>
    );
  }

  const sections = organizer.sections || [];
  const progress = getOverallProgress();
  const progressPct = progress.total > 0 ? Math.round((progress.answered / progress.total) * 100) : 0;
  const isCompleted = organizer.status === 'completed';

  return (
    <PageLayout padding={0} chromePadding={3}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Header */}
        <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', borderRadius: 0, px: 3, py: 2, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{ color: 'text.secondary', textTransform: 'none' }}
              >
                Back
              </Button>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{organizer.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {organizer.organizer_type_name} • Tax Year {organizer.tax_year}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {saving && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">Saving…</Typography>
                </Box>
              )}
              {!saving && lastSavedAt && (
                <Typography variant="caption" color="text.secondary">
                  {formatSavedAgo(lastSavedAt)}
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>

        {/* Main content: 7/5 columns */}
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Left column - Questions (7) */}
          <Box sx={{ flex: 7, minWidth: 0, overflow: 'auto', p: 3 }}>
            {sections.map((section) => {
              const prog = getSectionProgress(section);
              const visibleFields = (section.fields || []).filter(isFieldVisible);

              return (
                <Accordion
                  key={section.id}
                  expanded={expandedSection === section.id}
                  onChange={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                  sx={{
                    mb: 1,
                    '&:before': { display: 'none' },
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    boxShadow: 'none',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {section.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {prog.answered} of {prog.total} answered
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    {section.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {section.description}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {visibleFields.map((field) => (
                        <Box key={field.id}>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                            {field.label}
                            {field.is_required && <Typography component="span" color="error.main"> *</Typography>}
                          </Typography>
                          {field.help_text && <FormHelperText sx={{ mb: 0.5 }}>{field.help_text}</FormHelperText>}
                          {renderField(field)}
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>

          {/* Right column - Sidebar (5) */}
          <Box
            sx={{
              flex: 5,
              minWidth: 280,
              maxWidth: 400,
              flexShrink: 0,
              p: 2,
              borderLeft: 1,
              borderColor: 'divider',
              overflow: 'auto',
            }}
          >
            {/* Completion progress card */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Completion Progress
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress
                    variant="determinate"
                    value={progressPct}
                    size={80}
                    thickness={4}
                    sx={{ color: `${getProgressColor(progressPct)}.main` }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
                      {progressPct}%
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {progress.answered} of {progress.total} required fields answered
                </Typography>
              </Box>
            </Paper>

            {/* Due date + status */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {organizer.due_date && (
                  <Typography variant="body2" color="text.secondary">
                    Due: {new Date(organizer.due_date).toLocaleDateString()}
                  </Typography>
                )}
                <Chip
                  label={getStatusLabel(organizer.status)}
                  size="small"
                  color={getStatusColor(organizer.status)}
                  sx={{ alignSelf: 'flex-start' }}
                />
              </Box>
            </Paper>

            {/* Client Info card */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Client Info
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {organizer.client_name || '—'}
              </Typography>
            </Paper>

            {/* Attachments card */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                borderColor: 'divider',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Attachments
              </Typography>
              <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => document.getElementById('organizer-file-input')?.click()}
                sx={{
                  border: '2px dashed',
                  borderColor: dragOver ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  bgcolor: dragOver ? 'action.hover' : 'grey.50',
                  cursor: uploadLoading ? 'wait' : 'pointer',
                  mb: 2,
                }}
              >
                <input
                  id="organizer-file-input"
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    handleFileUpload(e.target.files);
                    e.target.value = '';
                  }}
                />
                {uploadLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <UploadIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Drag & drop or click
                    </Typography>
                  </>
                )}
              </Box>
              {uploadError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                  {uploadError}
                </Alert>
              )}
              {attachmentsLoading ? (
                <CircularProgress size={24} />
              ) : attachments.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No files yet
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {attachments.map((doc) => (
                    <Box
                      key={doc.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        py: 1,
                        px: 1.5,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" noWrap display="block">
                          {doc.filename}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(doc.file_size)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        {getDownloadUrl(doc) && (
                          <IconButton
                            size="small"
                            href={getDownloadUrl(doc)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'primary.main' }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton size="small" onClick={() => handleDeleteConfirm(doc)} sx={{ color: 'error.main' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Paper>
          </Box>
        </Box>

        {/* Bottom bar */}
        <Paper
          elevation={0}
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            px: 3,
            py: 2,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSaveProgress}
              disabled={saving}
              sx={{ textTransform: 'none' }}
            >
              Save Progress
            </Button>
            <Button
              variant="contained"
              startIcon={submitLoading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={handleSubmitOrganizer}
              disabled={submitLoading || isCompleted}
              sx={{ textTransform: 'none' }}
            >
              {isCompleted ? 'Submitted' : submitLoading ? 'Submitting…' : 'Submit Organizer'}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Dialog open={deleteConfirm.open} onClose={handleDeleteConfirmClose}>
        <DialogTitle>Delete attachment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{deleteConfirm.doc?.filename}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteConfirmClose}>Cancel</Button>
          <Button onClick={handleDeleteDocument} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={attachmentsSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setAttachmentsSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={attachmentsSnackbar.severity}
          onClose={() => setAttachmentsSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {attachmentsSnackbar.message}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

export default OrganizerDetail;
